import { useState, useEffect, useCallback, Dispatch, SetStateAction } from "react";
import scraperApi from "api";
import { useAbortController } from "hooks/useAbortController";
import { useLoading } from "hooks/useLoading";
import {
  ANALYTICS_FILTER_API_DEBOUNCE_MS,
  mapFilterOptionsToBackend,
  formatDateForBackend,
} from "utils/analytics";
import { DateRange } from "utils/analytics/types";

interface AvailableOptions {
  domains: string[];
  countries: string[];
  parameters: string[];
}

const EMPTY_OPTIONS: AvailableOptions = { domains: [], countries: [], parameters: [] };

interface FilterSelections {
  productType: string;
  parameters: string[];
  domains: string[];
  countries: string[];
}

/**
 * Manages fetching of analytics filter options (product types, domains, countries, parameters).
 *
 * Uses two separate concerns:
 * - Effect A (base options): fetches the full browsable lists for dropdowns.
 *   Only depends on dateRange + productType, so lists stay stable when
 *   the user selects individual domains/countries/parameters.
 * - Effect B (validation): when the user has active selections, sends all
 *   filters to the backend and auto-removes any selections the backend
 *   marks as invalid (e.g. domain doesn't exist for the selected country).
 */
export const useAnalyticsFilterOptions = (
  dateRange: DateRange,
  filters: FilterSelections,
  setDomains: Dispatch<SetStateAction<string[]>>,
  setCountries: Dispatch<SetStateAction<string[]>>,
) => {
  const { loading: isLoading, setLoading: setIsLoading } = useLoading();

  const [allProductTypes, setAllProductTypes] = useState<string[]>([]);
  const [baseOptions, setBaseOptions] = useState<AvailableOptions>(EMPTY_OPTIONS);

  const {
    createNewSignal: createBaseSignal,
    shouldIgnoreError: shouldIgnoreBaseError,
  } = useAbortController();
  const {
    createNewSignal: createValidationSignal,
    shouldIgnoreError: shouldIgnoreValidationError,
  } = useAbortController();
  const { createNewSignal: createSearchSignal } = useAbortController();

  // Effect A — Base options (product types + browsable dropdown lists)
  // Only depends on dateRange + productType so lists stay stable during selection changes.
  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) return;

    const fetchBase = async () => {
      const signal = createBaseSignal();
      setIsLoading(true);

      try {
        const backendFilters = mapFilterOptionsToBackend({
          productType: filters.productType === "All" ? null : filters.productType,
          parameters: [],
          domains: [],
          countries: [],
        });

        const response = await scraperApi.analytics.getFilterOptions(
          {
            startDate: formatDateForBackend(dateRange[0]),
            endDate: formatDateForBackend(dateRange[1]),
            ...backendFilters,
          },
          { signal },
        );

        if (signal.aborted) return;

        // Merge to prevent filtered fetches shrinking the master dropdown.
        const productTypes = response?.data?.productTypes || [];
        setAllProductTypes((prev) => Array.from(new Set([...prev, ...productTypes])));

        setBaseOptions({
          domains: response?.data?.domains || [],
          countries: response?.data?.countries || [],
          parameters: response?.data?.parameters || [],
        });
        setIsLoading(false);
      } catch (err: any) {
        if (shouldIgnoreBaseError(err)) return;
        // Clear loading so the modal's Save/Reset/dropdowns aren't permanently
        // disabled by a transient failure. Existing options (if any)
        // remain visible; otherwise dropdowns are empty until reopen/retry.
        setIsLoading(false);
        console.error("[useAnalyticsFilterOptions] Failed to fetch base options:", err);
      }
    };

    fetchBase();
  }, [dateRange, filters.productType, createBaseSignal, setIsLoading, shouldIgnoreBaseError]);

  // Effect B — Validation (auto-remove invalid selections)
  // Fires when any filter selection changes. Sends all active filters to the
  // backend and uses `invalidSelectedDomains` / `invalidSelectedCountries` to
  // silently remove selections that are no longer valid.
  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) return;

    const hasSelections =
      filters.domains.length > 0 ||
      filters.countries.length > 0 ||
      filters.parameters.length > 0;

    if (!hasSelections) return;

    const validate = async () => {
      const signal = createValidationSignal();

      try {
        const backendFilters = mapFilterOptionsToBackend({
          productType: filters.productType === "All" ? null : filters.productType,
          parameters: filters.parameters,
          domains: [],
          countries: [],
        });

        const response = await scraperApi.analytics.getFilterOptions(
          {
            startDate: formatDateForBackend(dateRange[0]),
            endDate: formatDateForBackend(dateRange[1]),
            ...backendFilters,
            ...(filters.domains.length > 0 && { selectedDomains: filters.domains }),
            ...(filters.countries.length > 0 && { selectedCountries: filters.countries }),
          },
          { signal },
        );

        if (signal.aborted) return;

        const invalidDomains = response?.data?.invalidSelectedDomains || [];
        setDomains((prev) => {
          const valid = prev.filter((d) => !invalidDomains.includes(d));
          return valid.length !== prev.length ? valid : prev;
        });

        const invalidCountries = response?.data?.invalidSelectedCountries || [];
        setCountries((prev) => {
          const valid = prev.filter((c) => !invalidCountries.includes(c));
          return valid.length !== prev.length ? valid : prev;
        });
      } catch (err: any) {
        if (shouldIgnoreValidationError(err)) return;
      }
    };

    const timer = setTimeout(validate, ANALYTICS_FILTER_API_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    dateRange,
    filters.productType,
    filters.domains,
    filters.countries,
    filters.parameters,
    createValidationSignal,
    shouldIgnoreValidationError,
    setDomains,
    setCountries,
  ]);

  const handleDomainSearch = useCallback(
    async (term: string): Promise<string[]> => {
      // Each new search aborts the previous in-flight request; ComboboxSelect
      // already ignores stale results, this just stops the wasted query
      const signal = createSearchSignal();
      try {
        const response = await scraperApi.analytics.searchDomains({
          startDate: dateRange[0] ? formatDateForBackend(dateRange[0]) : undefined,
          endDate: dateRange[1] ? formatDateForBackend(dateRange[1]) : undefined,
          search: term,
          limit: 20,
        }, { signal });
        return (response?.data || []).map((item) => item.domain);
      } catch {
        return [];
      }
    },
    [dateRange, createSearchSignal],
  );

  return {
    allProductTypes,
    availableOptions: baseOptions,
    isLoading,
    handleDomainSearch,
  };
};
